import React, { useState, useEffect } from 'react'
import { Helmet } from 'react-helmet-async'
import { useSearchParams } from 'react-router-dom'
import { useInspectorRef } from '../../../../hooks/useInspectorRef'
import {
  Box,
  Typography,
  Grid,
  Button,
  Alert,
  CircularProgress,
  Chip,
  Stack
} from '@mui/material'
import { FileDownload as DownloadIcon } from '@mui/icons-material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { enAU } from 'date-fns/locale'

import { useInspector } from '../../../../contexts/InspectorContext'
import { api } from '../../../../services/api'
import { formatCurrency as formatCurrencyFull } from '../../../../utils/inspectorFormatters'
import { LightspeedIcon, XeroIcon } from '../../../../components/SystemIcons'
import { poolwerxColors } from '../../../../theme/colors'
import { downloadBlob } from '../../../../utils/download'

const formatCurrency = (amount: number) =>
  formatCurrencyFull(amount, { minimumFractionDigits: 0, maximumFractionDigits: 0 })
import COGSAccountTable from './COGSAccountTable'

interface CogsComparisonData {
  month: string
  lightspeed_service_cogs: number
  lightspeed_retail_cogs: number
  lightspeed_total_cogs: number
  xero_closures_cogs: number
  xero_journals_cogs: number
  xero_total_cogs: number
  variance: number
  variance_percentage: number
  cross_month_cogs: number
}

interface ManualJournalDetail {
  date: string
  journal_number: string | null
  narration: string | null
  description: string | null
  amount: number
  account_code: string
  account_name?: string | null
}

interface CogsAccountReviewResponse {
  franchise_partner: {
    fp_code: string
    franchise_name: string
    country: string
    state: string
  }
  comparison_data: {
    start_month: string
    end_month: string
    comparison_data: CogsComparisonData[]
    manual_journal_details?: Record<string, ManualJournalDetail[]>
    other_cogs_journal_details?: Record<string, ManualJournalDetail[]>
    summary: {
      total_lightspeed_service_cogs: number
      total_lightspeed_retail_cogs: number
      total_lightspeed_cogs: number
      total_xero_closures_cogs: number
      total_xero_journals_cogs: number
      total_xero_cogs: number
      total_variance: number
      total_cross_month_cogs: number
    }
  }
  request_params: {
    start_month: string | null
    end_month: string | null
  }
}

const CogsAccountReview: React.FC = () => {
  console.log('🏗️ CogsAccountReview component rendering')
  
  const { fpCode: fp_code } = useInspectorRef()
  const [searchParams, setSearchParams] = useSearchParams()
  const { franchiseData } = useInspector()
  
  console.log('🔍 Component props:', { fp_code, franchiseData })

  // Get current date for month selector defaults
  const currentDate = new Date()
  const currentYear = currentDate.getFullYear()
  const currentMonth = currentDate.getMonth() // 0-based

  // Default to last 3 months
  const defaultEndMonth = new Date(currentYear, currentMonth - 1) // Previous month
  const defaultStartMonth = new Date(currentYear, currentMonth - 3) // 3 months ago

  // State for date range selection
  const [startMonth, setStartMonth] = useState<Date | null>(() => {
    const startParam = searchParams.get('start_month')
    if (startParam) {
      const [year, month] = startParam.split('-').map(Number)
      return new Date(year, month - 1)
    }
    return defaultStartMonth
  })
  const [endMonth, setEndMonth] = useState<Date | null>(() => {
    const endParam = searchParams.get('end_month')
    if (endParam) {
      const [year, month] = endParam.split('-').map(Number)
      return new Date(year, month - 1)
    }
    return defaultEndMonth
  })

  const [exportLoading, setExportLoading] = useState(false)
  const [exportError, setExportError] = useState<string | null>(null)

  const franchiseName = franchiseData?.franchise_name || 'Loading...'
  const pageTitle = `COGS Account Review | ${franchiseName} | Inspector Tool`

  // Fetch COGS comparison data - using direct useEffect approach
  const [data, setData] = useState<CogsAccountReviewResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchData = async () => {
      if (!fp_code) {
        return
      }
      
      setLoading(true)
      setError(null)
      
      try {
        const params = new URLSearchParams()
        if (startMonth) {
          const startMonthStr = `${startMonth.getFullYear()}-${String(startMonth.getMonth() + 1).padStart(2, '0')}`
          params.append('start_month', startMonthStr)
        }
        // Always include end_month, use current month if not set
        const finalEndMonth = endMonth || new Date(currentYear, currentMonth)
        const endMonthStr = `${finalEndMonth.getFullYear()}-${String(finalEndMonth.getMonth() + 1).padStart(2, '0')}`
        params.append('end_month', endMonthStr)
        
        const url = `/inspector/cogs-account-review/${fp_code}?${params.toString()}`
        
        const response = await api.get(url)
        setData(response)
      } catch (err: any) {
        setError(err.message || 'Failed to fetch data')
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [fp_code, startMonth, endMonth])

  // Date picker constraints
  const minDate = new Date(2024, 0) // January 2024
  const maxDate = new Date(currentYear, currentMonth) // Current month

  const handleStartMonthChange = (date: Date | null) => {
    setStartMonth(date)
    const newParams = new URLSearchParams(searchParams)
    if (date) {
      const startMonthStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
      newParams.set('start_month', startMonthStr)
    } else {
      newParams.delete('start_month')
    }
    // Always ensure end_month is in URL if it exists
    if (endMonth) {
      const endMonthStr = `${endMonth.getFullYear()}-${String(endMonth.getMonth() + 1).padStart(2, '0')}`
      newParams.set('end_month', endMonthStr)
    }
    setSearchParams(newParams)
  }

  const handleEndMonthChange = (date: Date | null) => {
    setEndMonth(date)
    const newParams = new URLSearchParams(searchParams)
    if (date) {
      const endMonthStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
      newParams.set('end_month', endMonthStr)
    } else {
      newParams.delete('end_month')
    }
    // Always ensure start_month is in URL if it exists
    if (startMonth) {
      const startMonthStr = `${startMonth.getFullYear()}-${String(startMonth.getMonth() + 1).padStart(2, '0')}`
      newParams.set('start_month', startMonthStr)
    }
    setSearchParams(newParams)
  }

  // Prepare table data
  const tableData = data?.comparison_data?.comparison_data || []
  const summary = data?.comparison_data?.summary
  
  // Determine if this is a US region (for Service vs Retail labeling)
  const isUSRegion = franchiseData?.country === 'US'

  const handleExport = async () => {
    if (!fp_code) return
    setExportLoading(true)
    setExportError(null)
    try {
      const params = new URLSearchParams()
      const startStr = startMonth
        ? `${startMonth.getFullYear()}-${String(startMonth.getMonth() + 1).padStart(2, '0')}`
        : null
      const endStr = endMonth
        ? `${endMonth.getFullYear()}-${String(endMonth.getMonth() + 1).padStart(2, '0')}`
        : null
      if (startStr) params.append('start_month', startStr)
      if (endStr) params.append('end_month', endStr)

      const filenameBase = `cogs-account-review-${fp_code}-${startStr ?? 'unknown'}-to-${endStr ?? 'unknown'}`
      await downloadBlob(
        `/inspector/cogs-account-review/export/${fp_code}?${params.toString()}`,
        filenameBase,
        'xlsx'
      )
    } catch (err: any) {
      console.error('Export failed:', err)
      setExportError(err?.message || 'Failed to export COGS account review')
    } finally {
      setExportLoading(false)
    }
  }

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
      </Helmet>

      <Box>
        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1 }}>
          <Typography variant="h4" component="h1">
            COGS Account Review
          </Typography>
          <Chip
            label="BETA"
            size="small"
            sx={{
              fontSize: '0.7rem',
              fontWeight: 700,
              letterSpacing: '0.05em',
              height: 24,
              bgcolor: 'warning.main',
              color: '#fff',
            }}
          />
        </Stack>

        <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
          Lines up the cost of sales out of Lightspeed against the COGS posted in Xero.{' '}
          Lightspeed register closures post to <strong>5000-100 Cost of Goods Sold</strong>{' '}
          automatically. The monthly <em>LS COGS Adjustment</em> journal should be posted to{' '}
          <strong>5000-200 COGS - Inventory Adjustments</strong> per the Poolwerx chart of accounts.
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 3, fontStyle: 'italic' }}>
          Note: adjustment journals on 5000-100 are still counted in the variance so the totals are
          correct, but flagged with ⚠️ for reposting to 5000-200. Journals on other 5000-* accounts
          (EOFY reallocations, opening/closing inventory) are listed separately and excluded from
          the comparison.
        </Typography>

        {/* Date Range Controls */}
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enAU}>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <DatePicker
                label="Start Month"
                value={startMonth}
                onChange={handleStartMonthChange}
                views={['year', 'month']}
                openTo="month"
                minDate={minDate}
                maxDate={maxDate}
                slotProps={{ textField: { size: 'small', fullWidth: true } }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <DatePicker
                label="End Month"
                value={endMonth}
                onChange={handleEndMonthChange}
                views={['year', 'month']}
                openTo="month"
                minDate={minDate}
                maxDate={maxDate}
                slotProps={{ textField: { size: 'small', fullWidth: true } }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Button
                variant="outlined"
                startIcon={exportLoading ? <CircularProgress size={20} /> : <DownloadIcon />}
                onClick={handleExport}
                disabled={exportLoading || !data}
                sx={{ height: '40px' }}
              >
                Export Excel
              </Button>
            </Grid>
          </Grid>
        </LocalizationProvider>

        {/* Summary Cards */}
        {summary && (
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={4}>
              <Box sx={{ 
                p: 2, 
                border: 1, 
                borderColor: 'divider', 
                borderRadius: 1,
                bgcolor: 'background.paper'
              }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LightspeedIcon size={20} />
                  Total Lightspeed COGS
                </Typography>
                <Typography variant="h6" color={poolwerxColors.teal} sx={{ fontWeight: 'bold' }}>
                  {formatCurrency(summary.total_lightspeed_cogs)}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ 
                p: 2, 
                border: 1, 
                borderColor: 'divider', 
                borderRadius: 1,
                bgcolor: 'background.paper'
              }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <XeroIcon size={20} />
                  Total Xero COGS
                </Typography>
                <Typography variant="h6" color={poolwerxColors.blue} sx={{ fontWeight: 'bold' }}>
                  {formatCurrency(summary.total_xero_cogs)}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ 
                p: 2, 
                border: 1, 
                borderColor: 'divider', 
                borderRadius: 1,
                bgcolor: 'background.paper'
              }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Total Variance
                </Typography>
                <Typography 
                  variant="h6" 
                  color={Math.abs(summary.total_variance) < 0.01 ? poolwerxColors.success : 
                         summary.total_variance > 0 ? 'warning.main' : 'error.main'}
                  sx={{ fontWeight: 'bold' }}
                >
                  {formatCurrency(summary.total_variance)}
                  {summary.total_lightspeed_cogs !== 0 && (
                    <span style={{ 
                      fontSize: '0.8em', 
                      marginLeft: '8px',
                      color: Math.abs(summary.total_variance / summary.total_lightspeed_cogs * 100) < 0.01 ? poolwerxColors.success :
                             (summary.total_variance / summary.total_lightspeed_cogs * 100) > 0 ? '#ff9800' : poolwerxColors.error
                    }}>
                      ({(summary.total_variance / summary.total_lightspeed_cogs * 100) >= 0 ? '+' : ''}{((summary.total_variance / summary.total_lightspeed_cogs * 100)).toFixed(1)}%)
                    </span>
                  )}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        )}

        {/* Error Display */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {exportError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setExportError(null)}>
            {exportError}
          </Alert>
        )}

        {/* Loading State */}
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        )}


        {/* Data Table */}
        {!loading && !error && data && (
          tableData.length > 0 ? (
            <COGSAccountTable
              data={tableData}
              fpCode={fp_code}
              isUSRegion={isUSRegion}
              loading={loading}
              manualJournalDetails={data.comparison_data.manual_journal_details}
              otherCogsJournalDetails={data.comparison_data.other_cogs_journal_details}
            />
          ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body1" color="text.secondary">
                No COGS comparison data available for the selected date range.
              </Typography>
            </Box>
          )
        )}
      </Box>
    </>
  )
}

export default CogsAccountReview